import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, hapusTerkonfirmasi, type Camera } from '../lib/api';
import { DialogHapus } from '../components/DialogHapus';
import { NomorHalaman, Pager, usePaging } from '../components/Pager';
import { PilihOrang } from '../components/PilihOrang';
import { useBahasa } from '../i18n';

/**
 * Pengelolaan dashboard — halaman **Camera Access** (§C2.1, §C2.2).
 *
 * Dulu tinggal di dalam halaman User sebagai deretan chip yang tiap kliknya
 * langsung menembakkan satu PUT. Tiga hal membuatnya menyusahkan: tidak ada
 * titik untuk membatalkan, tidak ada cara menghapus dashboard sama sekali, dan
 * halaman User jadi mengurus dua hal yang tidak berhubungan. Sekarang bentuknya
 * mengikuti halaman Kamera — daftar dengan Edit/Delete, dan satu formulir utuh
 * dengan satu tombol Save.
 */

const PER_PAGE = 15;

const LAYOUTS = ['1x1', '2x2', '3x3', '4x4', '5x5'] as const;
type LayoutName = (typeof LAYOUTS)[number];

const INTERVALS = [
  { s: null, label: 'Use the dashboard default (60s)' },
  { s: 15, label: '15 seconds' },
  { s: 30, label: '30 seconds' },
  { s: 45, label: '45 seconds' },
  { s: 60, label: '1 minute' },
  { s: 120, label: '2 minutes' },
  { s: 180, label: '3 minutes' },
  { s: 300, label: '5 minutes' },
];

interface DashboardRow {
  id: string;
  name: string;
  ownerUserId: string;
  layout: string;
  slideshowSeconds: number | null;
  isDefault: boolean;
  cameraIds: string[];
  assignedUserIds: string[];
}

interface UserRow {
  id: string;
  username: string;
  fullName: string;
}

interface Draft {
  name: string;
  ownerUserId: string;
  layout: LayoutName;
  slideshowSeconds: number | null;
  cameraIds: string[];
  assignedUserIds: string[];
}

const TOMBOL =
  'inline-flex min-h-tap items-center rounded-md border border-line px-3 text-sm transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.98]';

/** Kamera per halaman di dalam pemilih. Dua kolom × enam baris. */
const KAMERA_PER_HALAMAN = 12;

/**
 * Pemilih kamera: **cari, lalu satu halaman pada satu waktu** (§C2.2).
 *
 * Bentuk sebelumnya menurunkan seluruh 96 kamera sebagai kotak centang
 * sekaligus — tiga puluh dua baris yang mendorong tombol Save dan seluruh
 * bagian di bawahnya jauh ke luar layar. Yang hilang bukan cuma kenyamanan:
 * orang yang tidak menggulung sampai habis tidak pernah tahu bahwa masih ada
 * bagian di bawahnya.
 *
 * Tiga hal yang membuatnya tetap bisa dipakai sesudah dipendekkan:
 *
 * - **Kotak cari**, sama persis dengan `/cameras` dan bilah dashboard. Yang
 *   tahu nama kameranya tidak perlu paging sama sekali.
 * - **Ringkasan "n dipilih"** yang selalu terlihat, plus saringan "yang dipilih
 *   saja". Tanpa itu, pilihan yang ada di halaman 4 jadi tak terlihat dari
 *   halaman 1, dan orang menyimpan dashboard tanpa tahu isinya.
 * - **Tinggi tetap.** Kotaknya tidak tumbuh mengikuti jumlah kamera, jadi letak
 *   tombol Save tidak lagi bergantung pada berapa kamera yang terpasang.
 *
 * Pagingnya **murni di klien**: seluruh daftar sudah ada di memori halaman ini
 * sejak dimuat. Tidak ada permintaan jaringan, tidak ada perubahan URL, dan
 * tidak satu pun tombol di sini bertipe `submit` — menekan `›` tidak boleh
 * menyimpan formulir, apalagi memuat ulang halaman.
 */
function PilihKamera({
  cameras,
  selected,
  onChange,
}: {
  cameras: Camera[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useBahasa();
  const [cari, setCari] = useState('');
  const [hanyaTerpilih, setHanyaTerpilih] = useState(false);

  const terlihat = useMemo(() => {
    const q = cari.trim().toLowerCase();
    return cameras.filter(
      (c) =>
        (!hanyaTerpilih || selected.includes(c.id)) &&
        (q === '' || c.name.toLowerCase().includes(q)),
    );
  }, [cameras, cari, hanyaTerpilih, selected]);

  const paging = usePaging(terlihat, KAMERA_PER_HALAMAN);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  /** Pilih/kosongkan **yang sedang tersaring**, bukan seluruh armada. */
  const semuaTerlihatTerpilih =
    terlihat.length > 0 && terlihat.every((c) => selected.includes(c.id));

  return (
    <fieldset className="mt-4">
      <legend className="text-sm font-medium">{t('Cameras')}</legend>

      {cameras.length === 0 ? (
        <p className="mt-2 text-sm text-muted">{t('No cameras have been added yet.')}</p>
      ) : (
        <div className="mt-2 rounded-md border border-line">
          <div className="flex flex-wrap items-center gap-2 border-b border-line p-2">
            <input
              type="search"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
              placeholder={t('Search cameras')}
              aria-label={t('Search cameras')}
              // Alasan yang sama dengan kotak cari di pemilih orang: Enter di
              // kotak teks yang berada di dalam `<form>` memicu pengiriman
              // implisit, dan yang dimaksud orang saat menekannya di sini
              // adalah "saring", bukan "simpan".
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              className="min-h-tap w-full rounded-md border border-line bg-page px-3 text-sm sm:w-56"
            />
            <span className="text-sm text-muted" aria-live="polite">
              {t('{n} selected', { n: selected.length })}
            </span>
            <label className="flex min-h-tap cursor-pointer items-center gap-1.5 text-sm text-muted">
              <input
                type="checkbox"
                checked={hanyaTerpilih}
                onChange={(e) => setHanyaTerpilih(e.target.checked)}
                className="h-4 w-4 accent-[var(--color-accent-solid)]"
              />
              {t('Selected only')}
            </label>
            <button
              type="button"
              onClick={() =>
                semuaTerlihatTerpilih
                  ? onChange(selected.filter((id) => !terlihat.some((c) => c.id === id)))
                  : onChange([...new Set([...selected, ...terlihat.map((c) => c.id)])])
              }
              disabled={terlihat.length === 0}
              className="ml-auto text-sm text-accent-text underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
            >
              {semuaTerlihatTerpilih ? t('Clear these') : t('Select these')}
            </button>
          </div>

          {/* Tinggi minimum dipatok setinggi satu halaman penuh: tanpa itu,
              halaman terakhir yang cuma berisi dua baris membuat seluruh
              formulir di bawahnya melompat naik setiap kali `›` ditekan. */}
          <div className="grid min-h-[16.5rem] content-start gap-0.5 p-2 sm:grid-cols-2">
            {paging.slice.length === 0 ? (
              <p className="p-1 text-sm text-muted">
                {hanyaTerpilih && cari.trim() === ''
                  ? t('No camera has been picked yet.')
                  : t('No camera matches “{cari}”.', { cari: cari.trim() })}
              </p>
            ) : (
              paging.slice.map((c) => (
                <label
                  key={c.id}
                  className="flex min-h-tap cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-subtle"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    className="h-4 w-4 shrink-0 accent-[var(--color-accent-solid)]"
                  />
                  <span className="truncate">
                    {c.name}
                    {!c.isEnabled && <span className="ml-1 text-xs text-muted">{t('(disabled)')}</span>}
                  </span>
                </label>
              ))
            )}
          </div>

          {paging.pageCount > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line p-2">
              <span className="text-xs text-muted" aria-live="polite">
                {t('Page {page} of {pageCount} — {noun} {dari}–{sampai} of {total}', {
                  page: paging.page + 1,
                  pageCount: paging.pageCount,
                  noun: t('cameras'),
                  dari: paging.start + 1,
                  sampai: paging.start + paging.slice.length,
                  total: paging.total,
                })}
              </span>
              <NomorHalaman
                page={paging.page}
                pageCount={paging.pageCount}
                onPage={paging.setPage}
              />
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}

function DashboardForm({
  dashboard,
  users,
  cameras,
  busy,
  onSubmit,
  onCancel,
}: {
  dashboard: DashboardRow | null;
  users: UserRow[];
  cameras: Camera[];
  busy: boolean;
  onSubmit: (draft: Draft) => void;
  onCancel: () => void;
}) {
  const { t } = useBahasa();
  const [draft, setDraft] = useState<Draft>(() =>
    dashboard
      ? {
          name: dashboard.name,
          ownerUserId: dashboard.ownerUserId,
          layout: (LAYOUTS as readonly string[]).includes(dashboard.layout)
            ? (dashboard.layout as LayoutName)
            : '2x2',
          slideshowSeconds: dashboard.slideshowSeconds,
          cameraIds: dashboard.cameraIds,
          assignedUserIds: dashboard.assignedUserIds,
        }
      : {
          name: '',
          ownerUserId: users[0]?.id ?? '',
          layout: '2x2',
          slideshowSeconds: null,
          cameraIds: [],
          assignedUserIds: [],
        },
  );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const inputClass =
    'mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base text-content';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || !draft.name.trim() || !draft.ownerUserId) return;
        onSubmit({ ...draft, name: draft.name.trim() });
      }}
      className="rounded-lg border border-line bg-surface p-4"
    >
      <h2 className="text-lg">
        {dashboard ? t('Edit {nama}', { nama: dashboard.name }) : t('New camera group')}
      </h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="block text-sm">{t('Name')}</span>
          <input
            className={inputClass}
            required
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </label>

        <label className="block">
          <span className="block text-sm">{t('Owner')}</span>
          <select
            className={inputClass}
            value={draft.ownerUserId}
            onChange={(e) => set('ownerUserId', e.target.value)}
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username} — {u.fullName}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-sm">{t('Grid layout')}</span>
          <select
            className={inputClass}
            value={draft.layout}
            onChange={(e) => set('layout', e.target.value as LayoutName)}
          >
            {LAYOUTS.map((l) => (
              <option key={l} value={l}>
                {l.replace('x', '×')}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-sm">{t('Slideshow interval')}</span>
          <select
            className={inputClass}
            value={draft.slideshowSeconds ?? ''}
            onChange={(e) => set('slideshowSeconds', e.target.value ? Number(e.target.value) : null)}
          >
            {INTERVALS.map((i) => (
              <option key={i.label} value={i.s ?? ''}>
                {t(i.label)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <PilihKamera
        cameras={cameras}
        selected={draft.cameraIds}
        onChange={(ids) => set('cameraIds', ids)}
      />

      <PilihOrang
        legend={t('Who can see it')}
        hint={t('Type a name; the list is there to scroll through if you would rather look.')}
        semua={users.map((u) => ({
          id: u.id,
          username: u.username,
          fullName: u.fullName,
          catatan: u.id === draft.ownerUserId ? t('(owner)') : undefined,
        }))}
        terpilih={draft.assignedUserIds}
        onChange={(ids) => set('assignedUserIds', ids)}
        empty={t('No users yet.')}
      />

      {/* Bilah aksi menempel di dasar layar selama formulirnya lebih tinggi
          daripada jendela. Keluhan lapangan "menunya terlalu ke bawah" pada
          dasarnya soal ini: tombol Save yang letaknya bergantung pada berapa
          kamera dan berapa user yang kebetulan terdaftar. Sekarang letaknya
          tetap, dan dua daftar panjang di atasnya sudah tidak tumbuh lagi. */}
      <div className="sticky bottom-0 z-10 -mx-4 -mb-4 mt-6 flex gap-2 border-t border-line bg-surface px-4 py-3">
        <button
          type="submit"
          disabled={busy || !draft.name.trim() || !draft.ownerUserId}
          className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-60"
        >
          {busy ? t('Saving…') : t('Save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-tap rounded-md border border-line px-4 text-base"
        >
          {t('Cancel')}
        </button>
        <span className="ml-auto self-center text-xs text-muted">
          {t('{n} cameras', { n: draft.cameraIds.length })} ·{' '}
          {t('{n} people', { n: draft.assignedUserIds.length })}
        </span>
      </div>
    </form>
  );
}

export function DashboardsPage() {
  const { t } = useBahasa();
  const [dashboards, setDashboards] = useState<DashboardRow[] | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [editing, setEditing] = useState<DashboardRow | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [pesan, setPesan] = useState<{ jenis: 'ok' | 'gagal'; teks: string } | null>(null);
  /** Grup yang sedang dikonfirmasi penghapusannya (§G17). */
  const [akanDihapus, setAkanDihapus] = useState<DashboardRow | null>(null);

  const muat = useCallback(async () => {
    try {
      const [d, u, c] = await Promise.all([
        api<{ dashboards: DashboardRow[] }>('/dashboards'),
        api<{ users: UserRow[] }>('/users'),
        api<{ cameras: Camera[] }>('/cameras'),
      ]);
      setDashboards(d.dashboards);
      setUsers(u.users);
      // Kamera privat tidak boleh masuk dashboard — server menolaknya.
      setCameras(c.cameras.filter((k) => k.private === null));
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  const paging = usePaging(dashboards ?? [], PER_PAGE);

  const namaUser = useMemo(
    () => new Map(users.map((u) => [u.id, u.username])),
    [users],
  );

  async function simpan(draft: Draft) {
    setBusy(true);
    setPesan(null);
    const baru = editing === 'new';
    try {
      await api(baru ? '/dashboards' : `/dashboards/${(editing as DashboardRow).id}`, {
        method: baru ? 'POST' : 'PUT',
        body: JSON.stringify(draft),
      });
      setEditing(null);
      setPesan({
        jenis: 'ok',
        teks: baru ? t('Camera group created.') : t('Camera group saved.'),
      });
      await muat();
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  /**
   * `confirm()` bawaan browser sudah tidak dipakai di sini (§G17). Menghapus
   * satu grup mencabut akses **setiap orang yang ditugaskan padanya sekaligus**,
   * dan yang mereka lihat besok pagi cuma kamera yang tiba-tiba tidak ada.
   *
   * Galatnya dilempar, bukan ditulis ke banner: banner halaman ada di belakang
   * overlay dialog.
   */
  async function hapus(d: DashboardRow, konfirmasi: Parameters<typeof hapusTerkonfirmasi>[1]) {
    await hapusTerkonfirmasi(`/dashboards/${d.id}`, konfirmasi);
    setAkanDihapus(null);
    setPesan({ jenis: 'ok', teks: t('{nama} was deleted.', { nama: d.name }) });
    await muat();
  }

  if (!dashboards) {
    return (
      <div className="mx-auto max-w-5xl p-4">
        {pesan?.jenis === 'gagal' ? (
          <p role="alert" className="rounded-md border border-red-500 px-3 py-2 text-sm text-red-600">
            {pesan.teks}
          </p>
        ) : (
          <p className="text-sm text-muted" role="status">
            {t('Loading dashboards…')}
          </p>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <DashboardForm
          dashboard={editing === 'new' ? null : editing}
          users={users}
          cameras={cameras}
          busy={busy}
          onSubmit={simpan}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-xl">{t('Camera Access')}</h1>
        <button
          type="button"
          disabled={users.length === 0}
          onClick={() => setEditing('new')}
          className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-40"
        >
          {t('New camera group')}
        </button>
      </div>


      {pesan && (
        <p
          role={pesan.jenis === 'gagal' ? 'alert' : 'status'}
          className={`mt-4 rounded-md border px-3 py-2 text-sm ${
            pesan.jenis === 'gagal' ? 'border-red-500 text-red-600' : 'border-line text-muted'
          }`}
        >
          {pesan.teks}
        </p>
      )}

      {dashboards.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-line p-10 text-center">
          <p className="text-lg">{t('No camera groups yet')}</p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {paging.slice.map((d) => (
            <li key={d.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-base">{d.name}</h2>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                    <span>{d.layout.replace('x', '×')}</span>
                    <span>
                      {t('{n} cameras', { n: d.cameraIds.length })}
                    </span>
                    <span>
                      {t('owner {nama}', {
                        nama: namaUser.get(d.ownerUserId) ?? d.ownerUserId,
                      })}
                    </span>
                    {d.slideshowSeconds && (
                      <span>{t('slideshow {n}s', { n: d.slideshowSeconds })}</span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-xs text-muted">
                    {d.assignedUserIds.length === 0
                      ? t('Not assigned to anyone')
                      : t('Seen by {daftar}', {
                          daftar: d.assignedUserIds
                            .map((id) => namaUser.get(id) ?? id)
                            .join(', '),
                        })}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setEditing(d)} className={TOMBOL}>
                    {t('Edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAkanDihapus(d)}
                    className={`${TOMBOL} border-red-600 text-red-600 hover:bg-red-600/10`}
                  >
                    {t('Delete')}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {akanDihapus && (
        <DialogHapus
          nama={akanDihapus.name}
          judul={t('Delete camera group {nama}', { nama: akanDihapus.name })}
          keterangan={t(
            'Everyone assigned to it loses access to its cameras at once. The cameras and their recordings are not touched.',
          )}
          onBatal={() => setAkanDihapus(null)}
          onHapus={(k) => hapus(akanDihapus, k)}
        />
      )}

      <Pager
        page={paging.page}
        pageCount={paging.pageCount}
        start={paging.start}
        shown={paging.slice.length}
        total={paging.total}
        noun="camera groups"
        onPage={paging.setPage}
      />
    </div>
  );
}
